import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Minus, Plus } from 'lucide-react';
import { COMPARE, PRICE_TABS, PILLARS, FAQ, PAYG_FEATURES, ENT_FEATURES } from './paygData';

const PriceRow = ({ price, label }) => (
  <div className="py-prow"><b>{price}</b><span>{label}</span></div>
);

export const PlanPair = ({ id = 'pricing' }) => (
  <section id={id || undefined} className="py-section" data-testid="payg-plans">
    <div className="hm-container py-cards">
      <article className="py-card" data-testid="payg-plan-payg">
        <div className="py-panel">
          <h3>Pay as you go</h3>
          <p className="py-pitch">Launch your first agent today with $10 in free credit. The whole platform, no commitments. Build, test and only pay as you grow.</p>
          <PriceRow price="$0.11–$0.27" label="/ min for AI Voice Agents" />
          <PriceRow price="$0.005+" label="/ msg for AI Chat Agents" />
          <Link to="/signup?plan=launchpad" className="py-btn py-btn-dark" data-testid="payg-plan-cta-payg">Start pay as you go</Link>
        </div>
        <div className="py-feats"><p>Key features include</p><ul>{PAYG_FEATURES.map(f => <li key={f}><Check size={13} />{f}</li>)}</ul></div>
      </article>
      <article className="py-card py-card-ent" data-testid="payg-plan-enterprise">
        <div className="py-panel">
          <h3>Enterprise Plan</h3>
          <p className="py-pitch">Built for teams that need more reliability, compliance and hands-on support, with volume pricing that grows with you.</p>
          <PriceRow price="Custom Pricing" label="for AI Voice Agents" />
          <PriceRow price="Custom Pricing" label="for AI Chat Agents" />
          <Link to="/enterprise" className="py-btn py-btn-light" data-testid="payg-plan-cta-enterprise">Talk to our team</Link>
        </div>
        <div className="py-feats"><p>Everything in Pay as you go, plus</p><ul>{ENT_FEATURES.map(f => <li key={f}><Check size={13} />{f}</li>)}</ul></div>
      </article>
    </div>
  </section>
);

const Cell = ({ v }) => (v === true ? <span className="py-tick"><Check size={13} /></span> : v === false ? <span className="py-dash">—</span> : <span>{v}</span>);

export const ComparePlans = () => (
  <section className="py-section" data-testid="payg-compare">
    <div className="hm-container">
      <h2 className="py-h2">Compare plans</h2>
      <div className="py-cmp">
        <div className="py-cmp-head"><span />
          <div><p>Pay as you go</p><Link to="/signup?plan=launchpad" className="py-btn py-btn-dark py-btn-sm" data-testid="compare-cta-payg">Start pay as you go</Link></div>
          <div className="py-cmp-ent"><p>Enterprise</p><Link to="/enterprise" className="py-btn py-btn-light py-btn-sm" data-testid="compare-cta-enterprise">Talk to our team</Link></div>
        </div>
        {COMPARE.map(g => (
          <div key={g.group} className="py-cmp-group">
            <h3>{g.group}</h3>
            {g.rows.map(([k, a, b]) => <div key={g.group + k} className="py-cmp-row"><b>{k}</b><Cell v={a} /><Cell v={b} /></div>)}
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const PriceDetails = () => {
  const [tab, setTab] = useState(PRICE_TABS[0].id);
  const t = PRICE_TABS.find(x => x.id === tab);
  return (
    <section className="py-section" data-testid="payg-price-details">
      <div className="hm-container">
        <h2 className="py-h2">Detailed pricing</h2>
        <div className="py-tabs" role="tablist">
          {PRICE_TABS.map(x => <button key={x.id} role="tab" aria-selected={tab === x.id} className={tab === x.id ? 'on' : ''} onClick={() => setTab(x.id)} data-testid={`price-tab-${x.id}`}>{x.label}</button>)}
        </div>
        <div className="py-tabpanel" key={tab} data-testid={`price-panel-${tab}`}>
          <p className="py-muted">{t.note}</p>
          {t.rows.map(([k, v]) => <div key={k} className="py-drow"><span>{k}</span><b>{v}</b></div>)}
        </div>
      </div>
    </section>
  );
};

export const Advantages = () => (
  <section className="py-section py-dark" data-testid="payg-advantages">
    <div className="hm-container">
      <p className="py-eyebrow">Why agencies choose Voxinity</p>
      <h2 className="py-h2">More than voice minutes. A business in a box.</h2>
      <div className="py-pillars">{PILLARS.map(([t, d], i) => <div key={t} className="py-pillar"><span>{String(i + 1).padStart(2, '0')}</span><h3>{t}</h3><p>{d}</p></div>)}</div>
    </div>
  </section>
);

export const PaygFAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-section" data-testid="payg-faq">
      <div className="hm-container py-faq">
        <h2 className="py-h2">Frequently asked questions</h2>
        <div>{FAQ.map(([q, a], i) => (
          <div key={q} className={`py-q${open === i ? ' on' : ''}`} data-testid={`payg-faq-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)}>{q}{open === i ? <Minus size={16} /> : <Plus size={16} />}</button>
            {open === i && <p>{a}</p>}
          </div>))}
        </div>
      </div>
    </section>
  );
};
